import ApiService from '@/services/apiService';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { getTimeArrayFromRangeKey } from '@/components/core/dateTime/pickerParseRangeValues.js';
import cloneDeep from 'lodash/cloneDeep';

export default {
  data() {
    return {
      queryStartDate: new Date(new Date() - 86400000),
      queryEndDate: new Date(),
      customDatePicked: false,
      isLoadingChart: false,
      hasChartError: false,
      interval: '1h',
      latest: false,
      min: false,
      max: false,
      average: true,
      rawData: false,
      chartLinesCurved: true,
      chartOmitNull: true,
      span: 2,
      spanItems: [2, 3, 4, 5],
      queryTimeSpan: 'now-24h',
      minYAxis: null,
      maxYAxis: null,
      chartData: {
        data: {
          latest: [],
          avg: [],
          min: [],
          max: [],
          rawData: [],
        },
      },
      propVisualOpt: [
        'latest',
        'min',
        'max',
        'average',
        'span',
        'interval',
        'chartLinesCurved',
        'chartOmitNull',
        'queryTimeSpan',
        'queryStartDate',
        'queryEndDate',
        'minYAxis',
        'maxYAxis',
      ],
      extraParamOpt: ['interval', 'min', 'max'],
      multipleResponse: false,
      multiChartData: {},
    };
  },
  computed: {
    isEmptyChart() {
      return (chartObj) =>
        !this.isLoadingChart &&
        !this.hasChartError &&
        get(chartObj, 'data.data.latest.length') === 0 &&
        get(chartObj, 'data.data.avg.length') === 0 &&
        get(chartObj, 'data.data.min.length') === 0 &&
        get(chartObj, 'data.data.max.length') === 0 &&
        get(chartObj, 'data.data.rawData.length') === 0;
    },
    items() {
      return [
        { time: '5min', timeVal: '5m', disabled: false },
        { time: '15min', timeVal: '15m', disabled: false },
        { time: '1h', timeVal: '1h', disabled: false },
        { time: `1 ${this.$i18n_t('Common.day')}`, timeVal: '1d', disabled: false },
        // { time: `1 ${this.$i18n_t('Common.week')}`, timeVal: '1w', disabled: false },
      ];
    },
    chartConfig() {
      return {
        ...(this.chartLinesCurved
          ? {}
          : {
              elements: {
                line: {
                  tension: 0,
                },
              },
            }),
        ...(this.chartOmitNull ? {} : { spanGaps: true }),
      };
    },
  },
  watch: {
    interval: {
      handler(newVal) {
        this.setConfigState('interval', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    latest: {
      handler(newVal) {
        this.setConfigState('latest', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    min: {
      handler(newVal) {
        this.setConfigState('min', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    max: {
      handler(newVal) {
        this.setConfigState('max', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    average: {
      handler(newVal) {
        this.setConfigState('average', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    rawData: {
      handler(newVal) {
        this.setConfigState('rawData', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    span: {
      handler(newVal) {
        this.setConfigState('span', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    chartLinesCurved: {
      handler(newVal) {
        this.setConfigState('chartLinesCurved', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    chartOmitNull: {
      handler(newVal) {
        this.setConfigState('chartOmitNull', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    queryStartDate: {
      handler(newVal) {
        this.setConfigState('queryStartDate', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    queryEndDate: {
      handler(newVal) {
        this.setConfigState('queryEndDate', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    minYAxis: {
      handler(newVal) {
        this.setConfigState('minYAxis', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
    maxYAxis: {
      handler(newVal) {
        this.setConfigState('maxYAxis', newVal);
        this.loadChartDataDebounced(this.lclModel);
      },
    },
  },
  methods: {
    get,
    filteredDashboardData(dashboradData) {
      this.multipleResponse = true;
      return dashboradData.filter((item) => {
        if (has(item.properties.visual, 'type') && item.properties.visual.type === 'lineChart') {
          return item;
        }
      });
    },
    formatMultipleData(itemObj, resData) {
      Object.keys(resData).forEach((method) => {
        resData[method] = resData[method].map((item) => ({
          time: item.time,
          value:
            typeof item.value === 'number'
              ? parseFloat(item.value.toFixed(1))
              : itemObj.properties.valueType === 'boolean' && item.value === null
              ? 0
              : item.value,
        }));
      });
      const normalized = resData;

      let chartLabel = `${itemObj.properties.title ? itemObj.properties.title : ''} ${
        itemObj.properties.unit ? itemObj.properties.unit : ''
      }`;
      return { data: normalized, label: chartLabel };
    },
    setConfigState(objProp, propVal) {
      const clonedLclModel = cloneDeep(this.lclModel);
      clonedLclModel.properties.visual.options[objProp] = propVal;
      this.lclModel = clonedLclModel;
    },
    setConfigStateData(lclModelClone) {
      if (!lclModelClone) {
        lclModelClone = cloneDeep(this.lclModel);
      }

      if (!isEmpty(lclModelClone.properties.visual.options)) {
        this.propVisualOpt.forEach((value) => {
          this[value] = has(lclModelClone.properties.visual.options, value)
            ? lclModelClone.properties.visual.options[value]
            : this[value];
        });
      }
    },
    loadChartDataDebounced: debounce(function (objModel) {
      return this.loadChartData(objModel);
    }, 150),
    async loadChartData(objModel) {
      if (this.templateContext) {
        return;
      }
      this.isLoadingChart = true;
      this.hasChartError = false;

      let extraParams = {
        interval: get(objModel, 'properties.visual.options.interval') || this.interval,
        min: !!get(objModel, 'properties.visual.options.min'),
        max: !!get(objModel, 'properties.visual.options.max'),
        latest: !!get(objModel, 'properties.visual.options.latest'),
        rawData: !!get(objModel, 'properties.visual.options.rawData'),
      };

      if (this.multipleResponse) {
        this.extraParamOpt.forEach((item) => {
          extraParams[item] = has(objModel.properties.visual.options, item)
            ? objModel.properties.visual.options[item]
            : this[item];
        });
      }

      const queryTimeSpan = get(objModel, 'properties.visual.options.queryTimeSpan', this.queryTimeSpan);
      let timeSpanArray = getTimeArrayFromRangeKey(queryTimeSpan);

      // 1 metric needs to be loaded by default. Get the first one that was selected out of the three,
      // if none was selected, use 'avg' as default metric
      const defaultLoadingMetric =
        get(
          ['min', 'max', 'latest', 'rawData'].filter((m) => extraParams[m]),
          '[0]',
          null
        ) || 'avg';

      if (!objModel.dtwinId || !objModel.id) {
        return;
      }

      return ApiService.influxStatesSearchAggregated(
        this,
        [objModel.dtwinId],
        [objModel.id],
        {},
        new Date(timeSpanArray[0] * 1000).toISOString() || this.queryStartDate,
        new Date(timeSpanArray[1] * 1000).toISOString() || this.queryEndDate,
        // only load average data if it was selected in the first place
        // otherwise, just use min or max; which technically is requested already,
        // but it will appear in the resultset only once
        get(objModel, 'properties.visual.options.average') !== false ? 'avg' : defaultLoadingMetric,
        extraParams
      )
        .then((chartResponse) => {
          //Dashboard multiple chart tiles
          if (this.multipleResponse) {
            return { objModel, responseData: chartResponse };
          }

          const normalized = chartResponse;

          // convert all data values to fixed decimal places
          const chartData = Object.keys(normalized).reduce((acc, key) => {
            return {
              ...acc,
              [key]: normalized[key]
                .map((item) => ({
                  time: item.time,
                  value:
                    typeof item.value === 'number'
                      ? parseFloat(item.value.toFixed(1))
                      : objModel.properties.valueType === 'boolean' && item.value === null
                      ? 0
                      : item.value,
                }))
                // sort data descendingly
                // if there are too many datapoints, show the newest one first and cut off old data
                // https://digimondo.atlassian.net/browse/RD-6253
                .reverse(),
            };
          }, {});

          // set main label for chart based on stat title and unit
          this.chartData.label = `${objModel.properties.title ? objModel.properties.title : ''} ${
            objModel.properties.unit ? objModel.properties.unit : ''
          }`;

          // set chart data
          this.chartData.data = {
            ...(this.average ? { avg: chartData.avg.slice(0, 1000) } : {}),
            ...(this.max ? { max: chartData.max.slice(0, 1000) } : {}),
            ...(this.min ? { min: chartData.min.slice(0, 1000) } : {}),
            ...(this.latest ? { latest: chartData.latest.slice(0, 1000) } : {}),
            ...(this.rawData ? { rawData: chartData.rawData.slice(0, 1000) } : {}),
          };
        })
        .catch(() => {
          this.hasChartError = true;
        })
        .finally(() => {
          this.isLoadingChart = false;
        });
    },
    // this should be good to be removed as it does not look as it used anywhere where the mixin is called
    handleDatePicked(dateObject) {
      this.queryStartDate = new Date(dateObject.from * 1000);
      this.queryEndDate = new Date(dateObject.to * 1000);
      // NOTE: for the "reloadData" event, make sure that the end-date is always up-to-date
      // so refresh it automatically if it is "close enough"
      this.customDatePicked = Math.abs(this.queryEndDate - new Date()) > 60000;
      this.disableTimeIntervals(this.queryEndDate, this.queryStartDate);
    },
    // this `disableTimeIntervals` should be good to be removed completely
    // it is only called within this mixins `handleDatePicked` method which is not called at all in the mixin
    // and apparently nowhere else where the mixin is used
    disableTimeIntervals(endDate, startDate) {
      const diffInMils = new Date(endDate) - new Date(startDate);
      const diffInMins = Math.floor(diffInMils / 60000);
      const diffInDays = diffInMils / (1000 * 60 * 60 * 24);
      this.items.forEach((item) => {
        item.disabled = false;
      });
      if (diffInDays > 30) {
        // disable first three options
        this.items[0].disabled = true;
        this.items[1].disabled = true;
        this.items[2].disabled = true;
        this.interval = '1d';
      } else if (diffInDays < 30) {
        const durationsInMins = [5, 15, 60, 1440, 10080, 43200];
        let upperBoundDurationIndex = durationsInMins.findIndex((x) => diffInMins < x);
        // if smaller or equal to 5 then [5], otherwise the upperBoundDurationIndex.
        // example1: upperBoundDuration is 60 -> index: 2, then allowed is index 2
        let maximumAllowedDurationIndex = upperBoundDurationIndex ? upperBoundDurationIndex : 1;
        // follow example 1: allowedDurations = ['5m', '15m']
        this.items.forEach((item, itemIndex) => {
          if (itemIndex >= maximumAllowedDurationIndex) {
            item.disabled = true;
          }
        });

        // use the minimum between max allowed and stored interval
        // if max allowed is 1h and stored interval is 15m, then use 15m
        // if max allowed is 1h and stored interval is 1d, then use 1h
        const currentIntervalIndex = this.items.findIndex((x) => x.timeVal === this.interval);
        this.interval = this.items[Math.min(currentIntervalIndex, maximumAllowedDurationIndex - 1)].timeVal;
      }
    },
  },
};
